import React, { useState, useMemo, useEffect } from 'react';
import { Box, VStack, Text, Grid, Button, useToast, Flex, useBreakpointValue, Badge } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSound } from '../../contexts/SoundContext.tsx';
import { useTranslation } from 'react-i18next';
import { shuffleAnswers, getOriginalIndex, ShuffledAnswer } from '../../utils/shuffleAnswers.ts';
import { translateTexts } from '../../utils/translate.ts';
import TimerBar from '../Common/TimerBar.tsx';
import PlayerName from '../Common/PlayerName.tsx';

interface AnswerResult {
  isCorrect: boolean;
  selectedAnswer: string;
  correctAnswer: string;
  playerName: string;
  timestamp: number;
  correctIndexBase?: number; // 0-based
  selectedOriginalIndex?: number; // 0-based
}

interface MultiplayerQuestionCardProps {
  gameState: any;
  onAnswerQuestion: (answer: string, originalIndex?: number) => void;
  currentUser: any;
  answerResult?: AnswerResult | null;
  questionTimer?: { deadline: number; seconds: number; playerId: number; categoryId?: string } | null;
}

const MultiplayerQuestionCard: React.FC<MultiplayerQuestionCardProps> = ({ 
  gameState, 
  onAnswerQuestion, 
  currentUser,
  answerResult,
  questionTimer
}) => {
  const { t } = useTranslation();
  const { playSound } = useSound();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [displayQuestion, setDisplayQuestion] = useState<string | null>(null);
  const [displayOptions, setDisplayOptions] = useState<string[] | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  
  const buttonColumns = useBreakpointValue({ base: 1, md: 2 });
  const cardWidth = useBreakpointValue({ base: "90%", sm: "450px", md: "600px" });

  // Utiliser les catégories du gameState (récupérées depuis l'API)
  const categories = gameState.categories || [];

  // Log pour debug des images
  console.log('🎮 [MultiplayerQuestionCard] Current question:', gameState.currentQuestion);
  console.log('🎮 [MultiplayerQuestionCard] ImageUrl:', gameState.currentQuestion?.imageUrl);

  // Mélanger les réponses une seule fois quand la question change
  const baseQuestionAndOptions = useMemo(() => {
    if (!gameState.currentQuestion) return { text: '', options: [] as string[] };
    const q = gameState.currentQuestion;
    const text = q.question || q.question_text || '';
    let opts: string[] = [];
    if (q.options && Array.isArray(q.options)) opts = q.options;
    else if (q.option_1) opts = [q.option_1, q.option_2, q.option_3, q.option_4].filter(Boolean);
    else if (q.answers && Array.isArray(q.answers)) opts = q.answers;
    return { text, options: opts };
  }, [gameState.currentQuestion?.id]);

  const shuffledAnswers = useMemo(() => {
    if (!displayOptions) return [];
    return shuffleAnswers(displayOptions);
  }, [displayOptions, gameState.currentQuestion?.id]);

  // Traduction côté client si effet actif sur le joueur courant
  useEffect(() => {
    if (!gameState.currentQuestion) return;
    // Status effects are attached on gameState in multiplayer
    const statusEffects = (gameState as any).statusEffects || {};
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    // Support vertical server shape: translate: { [pid]: { remaining, lang } }
    const eff = statusEffects?.translate?.[currentPlayer?.id] || statusEffects?.[currentPlayer?.id]?.translate;
    const targetLang: string | undefined = eff?.remaining > 0 ? eff.lang : undefined;
    const baseText = baseQuestionAndOptions.text;
    const baseOpts = baseQuestionAndOptions.options;
    // Reset to base first
    setDisplayQuestion(baseText);
    setDisplayOptions(baseOpts);
    setIsTranslating(false);
    
    if (!targetLang || baseText.length === 0) return;
    // Avoid translating if already in same language
    setIsTranslating(true);
    translateTexts([baseText, ...baseOpts], targetLang)
      .then((res) => {
        const [q, ...opts] = res;
        setDisplayQuestion(q || baseText);
        setDisplayOptions(opts.length ? opts : baseOpts);
      })
      .catch(() => {
        // Fallback to base
        setDisplayQuestion(baseText);
        setDisplayOptions(baseOpts);
      })
      .finally(() => setIsTranslating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.currentQuestion?.id, gameState.players, gameState.currentPlayerIndex]);
  
  // Fonction pour obtenir les options de la question (pour compatibilité avec le code existant)
  const getQuestionOptions = () => {
    if (!gameState.currentQuestion) return [];
    const question = gameState.currentQuestion;
    
    // Si la question a déjà un format 'options', l'utiliser
    if (question.options && Array.isArray(question.options)) {
      return question.options;
    }
    
    // Si la question a le format API avec option_1, option_2, etc.
    if (question.option_1) {
      return [question.option_1, question.option_2, question.option_3, question.option_4].filter(Boolean);
    }
    
    // Si la question a le format ancien avec 'answers'
    if (question.answers && Array.isArray(question.answers)) {
      return question.answers;
    }
    
    // Fallback vide
    return [];
  };

  const questionOptions = displayOptions || getQuestionOptions();

  // Fonction pour obtenir le style d'un bouton selon les résultats
  const getButtonStyle = (answerText: string, idx: number) => {
    if (!answerResult) {
      // État normal - pas de résultat
      return {
        bg: "var(--gris-bleute)",
        color: "var(--texte-clair)",
        borderColor: "rgba(139, 148, 179, 0.3)",
        _hover: {
          bg: "var(--bleu-electrique)",
          borderColor: "var(--bleu-electrique)",
          transform: "translateY(-2px)",
        }
      };
    }

    // Comparer directement avec le texte de la réponse affichée
    // Le serveur devrait nous envoyer le texte exact des réponses
    // Préférence: utiliser les index lorsque disponibles (robuste aux traductions)
    const isCorrectAnswer = typeof answerResult.correctIndexBase === 'number'
      ? idx === answerResult.correctIndexBase
      : answerText === answerResult.correctAnswer;
    const isSelectedAnswer = typeof answerResult.selectedOriginalIndex === 'number'
      ? idx === answerResult.selectedOriginalIndex
      : answerText === answerResult.selectedAnswer;

    if (isCorrectAnswer) {
      // Bonne réponse - vert éclatant avec animation
      return {
        bg: "linear-gradient(135deg, #00FF88, #00CC6A)",
        color: "white",
        borderColor: "#00FF88",
        boxShadow: "0 0 20px rgba(0, 255, 136, 0.5)",
        transform: "scale(1.05)",
        fontWeight: "bold",
        _hover: {}
      };
    }

    if (isSelectedAnswer && !isCorrectAnswer) {
      // Mauvaise réponse sélectionnée - rouge vif avec animation
      return {
        bg: "linear-gradient(135deg, #FF4757, #FF3742)",
        color: "white",
        borderColor: "#FF4757",
        boxShadow: "0 0 20px rgba(255, 71, 87, 0.5)",
        transform: "scale(1.05)",
        fontWeight: "bold",
        _hover: {}
      };
    }

    // Autres réponses - style atténué
    return {
      bg: "var(--nuit-profonde)",
      color: "var(--texte-secondaire)",
      borderColor: "rgba(139, 148, 179, 0.1)",
      opacity: 0.6,
      _hover: {}
    };
  };

  if (!gameState.currentQuestion || !gameState.currentCategory) return null;

  // On récupère les infos de la catégorie depuis les catégories de l'API
  const category = categories.find(c => c.id === gameState.currentCategory);
  
  // Si la catégorie n'est pas trouvée, on utilise une catégorie par défaut
  const defaultCategory = { id: 'default', name: 'Catégorie', color: 'blue.400', is_mystery: false };
  const currentCategory = category || defaultCategory;

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isMyTurn = currentPlayer && currentPlayer.name === currentUser.displayName;
  const streak = currentPlayer.score[gameState.currentCategory] || 0;

  const handleAnswer = async (answer: string) => {
    if (selectedAnswer || !isMyTurn) return; // Empêche de répondre plusieurs fois ou si ce n'est pas son tour
    
    // Son de clic sur réponse
    playSound('buttonClick');
    
    // Trouver l'index original de la réponse sélectionnée
    const originalIndex = getOriginalIndex(shuffledAnswers, answer);
    
    setSelectedAnswer(answer);
    onAnswerQuestion(answer, originalIndex);
  };

  return (
    <Box
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      maxW={cardWidth}
      w="full"
      p={{ base: 4, md: 6 }}
      bg="var(--gris-bleute)"
      borderRadius="xl"
      boxShadow="2xl"
      border="1px solid"
      borderColor="rgba(139, 148, 179, 0.2)"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: currentCategory.color,
      }}
    >
      <VStack spacing={{ base: 4, md: 6 }}>
        {/* Timer de réponse à la question */}
        {questionTimer && (
          <Box w="full">
            <TimerBar
              deadline={questionTimer.deadline}
              seconds={questionTimer.seconds}
              color={currentCategory.color}
              labelPrefix={t('game.answerTimer') !== 'game.answerTimer' ? t('game.answerTimer') : t('answerTimer') || 'Temps de réponse'}
            />
          </Box>
        )}
        <Flex
          direction="column"
          align="center"
          bg="var(--nuit-profonde)"
          p={4}
          borderRadius="lg"
          w="full"
        >
          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="var(--texte-clair)">
              <PlayerName
                userId={currentPlayer.uid || (currentPlayer.name === currentUser.displayName ? currentUser.uid : '')}
                userName={currentPlayer.name}
                checkEffect={!!(currentPlayer.uid || (currentPlayer.name === currentUser.displayName && currentUser.uid))}
              />
            </Text>
          <Flex align="center" gap={3} mb={2}>
            
            {(gameState.currentQuestion.difficulty || gameState.currentQuestion.difficulty_level) && (
              <Badge 
                colorScheme={(v => { const x = Number(v)||1; return x<=2? 'green' : x<=4? 'yellow' : 'red'; })((gameState.currentQuestion.difficulty || gameState.currentQuestion.difficulty_level))}
                fontSize="xs"
              >
                {t('game.difficulty')} {(() => {
                  const v = Number(gameState.currentQuestion.difficulty || gameState.currentQuestion.difficulty_level) || 1;
                  const labels = ['Très facile','Facile','Moyen','Difficile','Très difficile'];
                  const clamp = (n: number) => Math.max(1, Math.min(5, Math.round(n)));
                  // If DB still returns 1..10 temporarily, map to 1..5
                  const five = v>5 ? Math.ceil(v/2) : v; 
                  return labels[clamp(five)-1];
                })()}
              </Badge>
            )}
            {/* Indicateur Hardcore si actif sur le joueur courant */}
            {(() => {
              const se = (gameState as any).statusEffects || {};
              const hardcore = se?.hardcore?.[currentPlayer?.id] || se?.[currentPlayer?.id]?.hardcore;
              if (hardcore && hardcore > 0) {
                return (
                  <Badge colorScheme="red" fontSize="xs">
                    🔥 Hardcore
                  </Badge>
                );
              }
              return null;
            })()}
          </Flex>
          <Text fontSize={{ base: "sm", md: "md" }} color="var(--texte-secondaire)">
            {currentCategory.name}
          </Text>
          {!currentCategory.is_mystery && (
            <Text fontSize="sm" color="var(--bleu-electrique)">
              {t('streak')}: {streak}/{gameState.streakToWin}
            </Text>
          )}
        </Flex>

        <Box textAlign="center" px={2}>
          <Text 
            fontSize={{ base: "lg", md: "xl" }} 
            fontWeight="semibold" 
            color="var(--texte-clair)"
            lineHeight="1.4"
          >
            {displayQuestion || gameState.currentQuestion.question || gameState.currentQuestion.question_text || t('game.questionUnavailable')}
          </Text>
        </Box>

        {/* Affichage de l'image si présente */}
        {gameState.currentQuestion?.imageUrl && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={4}
            mb={4}
          >
            <img
              src={gameState.currentQuestion.imageUrl}
              alt="Question illustration"
              style={{
                maxWidth: '300px',
                maxHeight: '200px',
                borderRadius: '8px',
                objectFit: 'contain',
                border: '2px solid var(--accent-color)',
              }}
              onError={(e) => {
                console.warn('❌ Image failed to load:', gameState.currentQuestion?.imageUrl);
                e.currentTarget.style.display = 'none';
              }}
              onLoad={() => {
                console.log('✅ Image loaded successfully:', gameState.currentQuestion?.imageUrl);
              }}
            />
          </Box>
        )}

        {answerResult && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              style={{ width: '100%' }}
            >
              <Flex
                direction="column"
                bg={answerResult.isCorrect ? "rgba(0, 255, 136, 0.1)" : "rgba(255, 71, 87, 0.1)"}
                border={`2px solid ${answerResult.isCorrect ? "#00FF88" : "#FF4757"}`}
                borderRadius="lg"
                p={4}
                align="center"
                gap={2}
              >
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color={answerResult.isCorrect ? "#00FF88" : "#FF4757"}
                >
                  {answerResult.isCorrect ? t('answerResult.correctTitle') : t('answerResult.incorrectTitle')}
                </Text>
                <Text color="var(--texte-clair)" fontSize="md">
                  {t('answerResult.correctAnswerLabel')} <strong>{answerResult.correctAnswer}</strong>
                </Text>
                {!answerResult.isCorrect && (
                  <Text color="var(--texte-secondaire)" fontSize="sm">
                    {t('answerResult.yourAnswerLabel')} {answerResult.selectedAnswer}
                  </Text>
                )}
                <Text color="var(--bleu-electrique)" fontSize="sm">
                  {t('answerResult.answeredBy')} {answerResult.playerName}
                </Text>
              </Flex>
            </motion.div>
          </AnimatePresence>
        )}

        <Grid
          templateColumns={`repeat(${buttonColumns}, 1fr)`}
          gap={3}
          w="full"
        >
          {shuffledAnswers.length > 0 ? (
            shuffledAnswers.map((shuffledAnswer, index) => {
              // L'index original (0-based) dans les options de base
              const idx0 = shuffledAnswer.originalIndex;
              const buttonStyle = getButtonStyle(shuffledAnswer.text, idx0);
              return (
                <Button
                  key={index}
                  onClick={() => handleAnswer(shuffledAnswer.text)}
                  size="lg"
                  minH="60px"
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="medium"
                  isDisabled={!isMyTurn || selectedAnswer !== null}
                  whiteSpace="normal"
                  textAlign="center"
                  p={2}
                  border="2px solid"
                  borderRadius="md"
                  transition="all 0.3s ease"
                  {...buttonStyle}
                >
                  {shuffledAnswer.text}
                </Button>
              );
            })
          ) : (
            <Text color="var(--texte-clair)" textAlign="center" gridColumn="1 / -1">
              {t('game.noOptionsAvailable')}
            </Text>
          )}
        </Grid>

        {isTranslating && (
          <Text fontSize="xs" color="var(--texte-secondaire)" textAlign="center">
            {t('game.translating') || 'Traduction en cours...'}
          </Text>
        )}

        {!isMyTurn && (
          <Text 
            fontSize="sm" 
            color="var(--texte-secondaire)" 
            textAlign="center"
            fontStyle="italic"
          >
            {t('waitingForPlayerToAnswer', { player: currentPlayer.name })}
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default MultiplayerQuestionCard;
